import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Trash2, Plus, Save, Wallet } from "lucide-react";
import { resellerApi } from "../lib/api";
import { fmtARS } from "../lib/utils-app";

const PKGS = ["pkg_110", "pkg_341", "pkg_572", "pkg_1166", "pkg_2398", "pkg_6160"];

function PriceEditor({ prices, onSaved }) {
  const [draft, setDraft] = useState(prices);
  useEffect(() => setDraft(prices), [prices]);
  async function save() {
    try {
      await resellerApi.adminPrices(draft);
      toast.success("Precios de revendedor guardados");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    }
  }
  return (
    <div className="mt-4">
      <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Precios revendedor (ARS)</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {PKGS.map((k) => (
          <label key={k} className="text-xs text-neutral-400">
            {k.replace("pkg_", "")} diamantes
            <input type="number" value={draft[k] ?? ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
              data-testid={`reseller-price-${k}`} className="mt-1 w-full px-2 py-2 rounded-md bg-black border border-white/10 text-white text-sm" />
          </label>
        ))}
      </div>
      <button onClick={save} data-testid="reseller-prices-save" className="mt-3 btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-1.5">
        <Save className="w-4 h-4" /> Guardar precios
      </button>
    </div>
  );
}

function ResellerRow({ r, onChange }) {
  async function adjust() {
    const v = window.prompt(`Ajustar saldo de ${r.email} (ej: 5000 o -5000)`);
    if (v === null) return;
    const delta = parseInt(v, 10);
    if (!delta) return toast.error("Monto inválido");
    try {
      await resellerApi.adminAdjust(r.email, delta, "Ajuste manual");
      toast.success("Saldo actualizado");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  }
  async function remove() {
    if (!window.confirm(`¿Quitar acceso de revendedor a ${r.email}? El saldo se conserva.`)) return;
    await resellerApi.adminRemove(r.email);
    toast.success("Acceso quitado");
    onChange();
  }
  return (
    <div className="flex flex-wrap items-center gap-3 px-3 py-2.5" data-testid={`reseller-row-${r.email}`}>
      <div className="min-w-0 flex-1 text-sm text-white break-all">{r.email}</div>
      <div className="text-amber-300 font-semibold text-sm" data-testid={`reseller-row-balance-${r.email}`}>{fmtARS(r.balance_ars || 0)}</div>
      <button onClick={adjust} data-testid={`reseller-adjust-${r.email}`} className="px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-xs flex items-center gap-1">
        <Wallet className="w-3.5 h-3.5" /> Ajustar
      </button>
      <button onClick={remove} data-testid={`reseller-remove-${r.email}`} className="p-1.5 rounded-md hover:bg-rose-500/20 text-rose-300" aria-label="Quitar">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function ResellersAdmin() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const load = () => resellerApi.adminList().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);

  async function add() {
    try {
      await resellerApi.adminAdd(email);
      toast.success("Revendedor agregado");
      setEmail("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  }

  if (!data) return null;
  return (
    <div className="mb-6 rounded-xl border border-amber-400/30 bg-[#0d0d0d] p-4" data-testid="resellers-admin">
      <div className="flex items-center gap-2 font-semibold text-white">
        <Users className="w-5 h-5 text-amber-300" /> Revendedores <span className="text-xs text-neutral-500 font-normal">· carga mínima {fmtARS(data.min_topup)}</span>
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 max-w-lg">
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="correo@gmail.com" data-testid="reseller-email-input"
          className="min-w-0 px-3 py-2 rounded-md bg-black border border-white/10 text-white text-sm" />
        <button onClick={add} disabled={!email.includes("@")} data-testid="reseller-add-btn"
          className="px-4 py-2 rounded-md bg-amber-400 text-black text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-50">
          <Plus className="w-4 h-4" /> Agregar
        </button>
      </div>
      <div className="mt-3 rounded-lg border border-white/5 divide-y divide-white/5">
        {data.resellers.length ? data.resellers.map((r) => <ResellerRow key={r.email} r={r} onChange={load} />)
          : <div className="px-3 py-3 text-sm text-neutral-500" data-testid="resellers-empty">Sin revendedores todavía.</div>}
      </div>
      <PriceEditor prices={data.prices} onSaved={load} />
    </div>
  );
}
